<script>
import { helpers as notificationHelpers } from '@/store/modules/notifications';
import { SHOW_SNACKBAR } from '@/store/types';
import Api from '@/utils/api';
import { exists, invalidNameMessage, isEmail } from '@/utils/validationRules';
import Storage from 'Shared/helpers/storageManager';
import _ from 'lodash';

export default {
  data: () => ({
    email: '',
    firstName: '',
    accepted: false,
    formIsValid: false,
    isSubmitting: false,
    lastName: '',
    rules: {
      exists: () => true,
      isEmail: () => true,
      invalidNameMessage: () => true,
    },
  }),
  computed: {
    formattedEmail() {
      return _.chain(this.email).trim().toLower().value();
    },
  },
  mounted() {
    Storage.participantSignup.setJSON({}); // clear any previous signups
  },
  methods: {
    ...notificationHelpers.mapActions({ showSnackbar: SHOW_SNACKBAR }),
    handleSignupError(message) {
      this.showSnackbar({ message });
      this.isSubmitting = false;
    },
    saveParamsToLocalStorage() {
      Storage.participantSignup.setJSON({
        email: this.formattedEmail,
        firstName: this.firstName,
        lastName: this.lastName,
      });
    },
    setRules() {
      this.rules = {
        exists,
        isEmail,
        invalidNameMessage,
      };
    },
    async signupParticipant() {
      this.setRules();
      this.$nextTick(async () => {
        await this.$refs.form.validate();
        if (!this.formIsValid) {
          return;
        }
        this.isSubmitting = true;

        const { domainEmail, domainError } = await this.verifyEmailDomain();
        if (domainError) {
          return this.handleSignupError(domainError);
        }
        if (domainEmail) {
          const message = 'Unable to validate email address.';
          return this.handleSignupError(message);
        }

        const { emailUnique, error } = await this.verifyEmailUnique();
        if (error) {
          return this.handleSignupError(error);
        }
        if (!emailUnique) {
          const message =
            'It looks like you already have an account with this email. Please sign in instead.';
          return this.handleSignupError(message);
        }

        this.saveParamsToLocalStorage();

        const url = `https://${window.location.host}/respondents/v2/personal?onboarding=true`;
        window.location.assign(url);
      });
    },
    async verifyEmailDomain() {
      try {
        const {
          data: { response },
        } = await Api.post('/v2/verify-email-domain', {
          email: this.formattedEmail,
        });
        return { domainEmail: response.isDomainBlocked };
      } catch (e) {
        return {
          error: 'There was a problem signing you up. Please try again later.',
        };
      }
    },
    async verifyEmailUnique() {
      try {
        const {
          data: { response },
        } = await Api.post('/v2/verify-email-unique', {
          email: this.formattedEmail,
        });
        return { emailUnique: response.isEmailUnique };
      } catch (e) {
        return {
          error: 'There was a problem signing you up. Please try again later.',
        };
      }
    },
  },
};
</script>

<template>
  <div>
    <v-form
      v-model="formIsValid"
      method="POST"
      @submit.prevent="signupParticipant"
      ref="form"
    >
      <v-row>
        <v-col>
          <base-input
            v-model="firstName"
            label="First Name"
            name="firstName"
            placeholder="Jane"
            :rules="[rules.exists, rules.invalidNameMessage]"
            type="text"
            :value="firstName"
          />
        </v-col>

        <v-col>
          <base-input
            v-model="lastName"
            label="Last Name"
            name="lastName"
            placeholder="Doe"
            :rules="[rules.exists, rules.invalidNameMessage]"
            type="text"
            :value="lastName"
          />
        </v-col>
      </v-row>

      <base-input
        v-model="email"
        label="Email"
        name="email"
        placeholder="name@emaildomain.com"
        :rules="[rules.exists, rules.isEmail]"
        type="text"
        :value="email"
      />

      <v-checkbox
        class="accept-terms-and-policy"
        v-model="accepted" :rules="[v => !!v || '']"
        style="--v-input-control-margin-bottom: 0px;"
      >
        <template v-slot:label>
          <div class="text-caption mb-0">
            I agree to Respondent's
            <a
              href="https://www.respondent.io/participant-terms-conditions"
              target="_new"
              rel="noopener noreferrer"
              @click.stop
              >Terms & Conditions</a
            >
            and
            <a
              href="https://www.respondent.io/privacy-policy"
              target="_new"
              rel="noopener noreferrer"
              @click.stop
              >Privacy Policy</a
            >
          </div>
        </template>
      </v-checkbox>

      <v-btn
        block
        color="primary"
        type="submit"
        :loading="isSubmitting"
      >
        Create an Account
      </v-btn>
    </v-form>
  </div>
</template>

<style scoped>

.accept-terms-and-policy{
  margin-top: -8px;
  margin-bottom: -12px;
}
</style>
